/**
 * ReportsPage exposes operational exports off the trip_instance_stop_times
 * ledger: per-stop arrival/departure events and per-trip summaries. The
 * preview table renders the same JSON the CSV download contains; "Download
 * CSV" hits the same endpoint with format=csv.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { Driver, Route, Vehicle } from "../api/types";
import { fetchStopEventsReport, fetchTripSummaryReport, downloadReportCsv, downloadStioXlsx, detectTrips, type StopEventReportRow, type TripSummaryReportRow } from "../api/reports";

type ReportKind = "stop-events" | "trip-summary";

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function weekAgoIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtIsoTime(iso?: string | null): string {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

// Matches the backend's own definition of "late" (tripSummaryBase's
// `stops_late` computation: arrival_delay_seconds > 300) so the KPI cards,
// the hourly chart and the raw stops_late column below always agree.
const ON_TIME_THRESHOLD_SEC = 300;

function daysAgoIso(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function lastNDays(n: number): string[] {
  return Array.from({ length: n }, (_, i) => daysAgoIso(n - 1 - i));
}

function Sparkline({ values, stroke }: { values: number[]; stroke: string }) {
  if (values.length < 2 || values.every((v) => v === values[0])) {
    return <svg width={64} height={24} viewBox="0 0 64 24" className="reports-spark" />;
  }
  const w = 64, h = 24, pad = 2;
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * (w - pad * 2) + pad;
      const y = h - pad - ((v - min) / range) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="reports-spark">
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function ReportsPage() {
  const [kind, setKind] = useState<ReportKind>("stop-events");
  const [dateFrom, setDateFrom] = useState<string>(weekAgoIso());
  const [dateTo, setDateTo] = useState<string>(todayIso());

  const [routes, setRoutes] = useState<Route[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);

  const [routeId, setRouteId] = useState<string>("");
  const [vehicleId, setVehicleId] = useState<string>("");
  const [driverId, setDriverId] = useState<string>("");

  const [stopRows, setStopRows] = useState<StopEventReportRow[]>([]);
  const [tripRows, setTripRows] = useState<TripSummaryReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Defensive unwrap: some legacy endpoints still return a placeholder
    // {status,message,endpoint} body. Coerce anything that is not an
    // array-or-`{data:[]}` into [] so the .map below cannot crash and
    // blank the OCC page.
    const toArray = <T,>(v: unknown): T[] => {
      if (Array.isArray(v)) return v as T[];
      if (v && typeof v === "object" && Array.isArray((v as { data?: unknown }).data)) {
        return (v as { data: T[] }).data;
      }
      return [];
    };
    Promise.all([
      api.listRoutes().catch(() => [] as Route[]),
      api.listVehicles().catch(() => [] as Vehicle[]),
      api.listDrivers().catch(() => [] as Driver[]),
    ]).then(([rts, vhs, drs]) => {
      if (cancelled) return;
      setRoutes(toArray<Route>(rts));
      setVehicles(toArray<Vehicle>(vhs));
      setDrivers(toArray<Driver>(drs));
    });
    return () => { cancelled = true; };
  }, []);

  // Overview hero: KPI cards + hourly chart, fetched independently of the
  // filtered report browser below. 30 days back covers all three range
  // toggles (Today/7 days/30 days) from a single pair of real queries.
  const [heroStopEvents, setHeroStopEvents] = useState<StopEventReportRow[]>([]);
  const [heroTripSummary, setHeroTripSummary] = useState<TripSummaryReportRow[]>([]);
  const [heroLoading, setHeroLoading] = useState(false);
  const [hourlyRange, setHourlyRange] = useState<"today" | "7d" | "30d">("today");

  useEffect(() => {
    let cancelled = false;
    setHeroLoading(true);
    const range = { date_from: daysAgoIso(29), date_to: todayIso() };
    Promise.all([
      fetchStopEventsReport(range).catch(() => [] as StopEventReportRow[]),
      fetchTripSummaryReport(range).catch(() => [] as TripSummaryReportRow[]),
    ]).then(([stops, trips]) => {
      if (cancelled) return;
      setHeroStopEvents(stops);
      setHeroTripSummary(trips);
      setHeroLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const todayStr = todayIso();
  const todayTrips = useMemo(
    () => heroTripSummary.filter((r) => r.service_date.slice(0, 10) === todayStr),
    [heroTripSummary, todayStr]
  );
  const todayStops = useMemo(
    () => heroStopEvents.filter((r) => r.service_date.slice(0, 10) === todayStr && r.arrival_delay_seconds != null),
    [heroStopEvents, todayStr]
  );
  const measuredTripsCount = todayTrips.filter((r) => r.avg_delay_seconds != null).length;
  const onTimePct = todayStops.length
    ? Math.round((todayStops.filter((r) => (r.arrival_delay_seconds ?? 0) <= ON_TIME_THRESHOLD_SEC).length / todayStops.length) * 100)
    : null;
  const avgDelaySec = todayStops.length
    ? todayStops.reduce((a, r) => a + (r.arrival_delay_seconds ?? 0), 0) / todayStops.length
    : null;
  const completedCount = todayTrips.filter((r) => r.status === "completed").length;
  const noShowCount = todayTrips.filter((r) => r.status === "no_show").length;
  const cancelledCount = todayTrips.filter((r) => r.status === "cancelled").length;
  // Started but never reached the final stop — closed by the server, not the
  // driver. Not a cancellation, and not a completion either.
  const incompleteCount = todayTrips.filter((r) => r.status === "incomplete").length;

  const days7 = useMemo(() => lastNDays(7), []);
  const dayOnTimePct = (day: string) => {
    const rows = heroStopEvents.filter((r) => r.service_date.slice(0, 10) === day && r.arrival_delay_seconds != null);
    return rows.length ? (rows.filter((r) => (r.arrival_delay_seconds ?? 0) <= ON_TIME_THRESHOLD_SEC).length / rows.length) * 100 : 0;
  };
  const dayAvgDelayMin = (day: string) => {
    const rows = heroStopEvents.filter((r) => r.service_date.slice(0, 10) === day && r.arrival_delay_seconds != null);
    return rows.length ? rows.reduce((a, r) => a + (r.arrival_delay_seconds ?? 0), 0) / rows.length / 60 : 0;
  };
  const dayCompletedCount = (day: string) =>
    heroTripSummary.filter((r) => r.service_date.slice(0, 10) === day && r.status === "completed").length;
  const dayCancellations = (day: string) =>
    heroTripSummary.filter((r) => r.service_date.slice(0, 10) === day && (r.status === "cancelled" || r.status === "no_show")).length;

  const onTimeSeries = useMemo(() => days7.map(dayOnTimePct), [days7, heroStopEvents]);
  const avgDelaySeries = useMemo(() => days7.map(dayAvgDelayMin), [days7, heroStopEvents]);
  const completedSeries = useMemo(() => days7.map(dayCompletedCount), [days7, heroTripSummary]);
  const cancellationsSeries = useMemo(() => days7.map(dayCancellations), [days7, heroTripSummary]);

  const hourlyDays = useMemo(
    () => lastNDays(hourlyRange === "today" ? 1 : hourlyRange === "7d" ? 7 : 30),
    [hourlyRange]
  );
  const hourlyBuckets = useMemo(() => {
    const daySet = new Set(hourlyDays);
    const buckets: Record<number, { total: number; onTime: number }> = {};
    for (const r of heroStopEvents) {
      if (r.arrival_delay_seconds == null) continue;
      if (!daySet.has(r.service_date.slice(0, 10))) continue;
      const anchor = r.scheduled_arrival || r.actual_arrival;
      if (!anchor) continue;
      const hour = new Date(anchor).getHours();
      if (Number.isNaN(hour)) continue;
      const b = (buckets[hour] ??= { total: 0, onTime: 0 });
      b.total++;
      if ((r.arrival_delay_seconds ?? 0) <= ON_TIME_THRESHOLD_SEC) b.onTime++;
    }
    return buckets;
  }, [heroStopEvents, hourlyDays]);
  const hourlyHours = Object.keys(hourlyBuckets).map(Number).sort((a, b) => a - b);

  const exportOverviewCsv = useCallback(() => {
    downloadReportCsv("trip-summary", { date_from: todayStr, date_to: todayStr });
  }, [todayStr]);

  const filterParams = useMemo(() => ({
    date_from: dateFrom,
    date_to: dateTo,
    route_id: routeId || undefined,
    vehicle_id: vehicleId || undefined,
    driver_id: driverId || undefined,
  }), [dateFrom, dateTo, routeId, vehicleId, driverId]);

  const runReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (kind === "stop-events") {
        const rows = await fetchStopEventsReport(filterParams);
        setStopRows(rows);
        setTripRows([]);
      } else {
        const rows = await fetchTripSummaryReport(filterParams);
        setTripRows(rows);
        setStopRows([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [kind, filterParams]);

  const downloadCsv = useCallback(() => {
    downloadReportCsv(kind, filterParams);
  }, [kind, filterParams]);

  // Stop In/Out is a per-route, per-direction, per-day workbook rather than a
  // filtered range, so it gets its own controls instead of reusing the range
  // filters above.
  const [stioDirection, setStioDirection] = useState<0 | 1>(0);
  const [stioBusy, setStioBusy] = useState(false);
  const [stioNote, setStioNote] = useState<string | null>(null);

  const downloadStio = useCallback(async () => {
    if (!routeId) {
      setError("Select a route — Stop In/Out is produced one route and direction at a time.");
      return;
    }
    setError(null);
    setStioBusy(true);
    try {
      await downloadStioXlsx({ route_id: routeId, direction_id: stioDirection, service_date: dateTo });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to build workbook");
    } finally {
      setStioBusy(false);
    }
  }, [routeId, stioDirection, dateTo]);

  const runDetection = useCallback(async () => {
    setError(null);
    setStioBusy(true);
    setStioNote(null);
    try {
      const res = await detectTrips(dateTo);
      setStioNote(
        `Detected ${res.trips_detected} trip(s) across ${res.vehicles_scanned} vehicle(s); ` +
        `${res.reconciled} matched a declared trip, ${res.flagged} flagged.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Detection failed");
    } finally {
      setStioBusy(false);
    }
  }, [dateTo]);

  return (
    <div className="page reports-page">
      <div className="reports-header">
        <div>
          <h1>Reports</h1>
          <p className="secondary-text">Punctuality, delays and completed service.</p>
        </div>
        <div className="reports-header-actions">
          <button className="btn reports-export-btn" type="button" onClick={exportOverviewCsv} disabled={heroLoading}>
            Export CSV
          </button>
          <button className="btn reports-export-btn" type="button" onClick={() => window.print()}>
            Export PDF
          </button>
        </div>
      </div>

      <div className="reports-stat-grid">
        <div className="card reports-stat-card">
          <div className="reports-stat-head">
            <span className="reports-stat-icon ok">⏱</span>
            <span className="reports-stat-label">On-time performance</span>
          </div>
          <div className="reports-stat-row">
            <span className="reports-stat-value">{onTimePct != null ? `${onTimePct}%` : "—"}</span>
            <Sparkline values={onTimeSeries} stroke="var(--sev-ok)" />
          </div>
          <span className="reports-stat-foot ok">target ≥ 90%</span>
        </div>

        <div className="card reports-stat-card">
          <div className="reports-stat-head">
            <span className="reports-stat-icon info">∿</span>
            <span className="reports-stat-label">Avg delay</span>
          </div>
          <div className="reports-stat-row">
            <span className="reports-stat-value">{avgDelaySec != null ? `${(avgDelaySec / 60).toFixed(1)}m` : "—"}</span>
            <Sparkline values={avgDelaySeries} stroke="var(--sev-info)" />
          </div>
          <span className="reports-stat-foot muted">across {measuredTripsCount} measured trips</span>
        </div>

        <div className="card reports-stat-card">
          <div className="reports-stat-head">
            <span className="reports-stat-icon ok">✓</span>
            <span className="reports-stat-label">Trips completed</span>
          </div>
          <div className="reports-stat-row">
            <span className="reports-stat-value">{completedCount}</span>
            <Sparkline values={completedSeries} stroke="var(--sev-ok)" />
          </div>
          <span className="reports-stat-foot ok">
            of {todayTrips.length} scheduled today{incompleteCount > 0 ? ` · ${incompleteCount} incomplete` : ""}
          </span>
        </div>

        <div className="card reports-stat-card">
          <div className="reports-stat-head">
            <span className="reports-stat-icon warn">⚠</span>
            <span className="reports-stat-label">Cancellations</span>
          </div>
          <div className="reports-stat-row">
            <span className="reports-stat-value">{noShowCount + cancelledCount}</span>
            <Sparkline values={cancellationsSeries} stroke="var(--sev-warn)" />
          </div>
          <span className="reports-stat-foot warn">{noShowCount} no-show · {cancelledCount} cancelled</span>
        </div>
      </div>

      <section className="card reports-hourly-card">
        <div className="reports-hourly-head">
          <h3><span className="reports-bar" />On-time performance by hour</h3>
          <div className="reports-segmented">
            <button type="button" className={hourlyRange === "today" ? "active" : ""} onClick={() => setHourlyRange("today")}>Today</button>
            <button type="button" className={hourlyRange === "7d" ? "active" : ""} onClick={() => setHourlyRange("7d")}>7 days</button>
            <button type="button" className={hourlyRange === "30d" ? "active" : ""} onClick={() => setHourlyRange("30d")}>30 days</button>
          </div>
        </div>

        {hourlyHours.length === 0 ? (
          <div className="empty">{heroLoading ? "Loading…" : "No measured stop events in this window."}</div>
        ) : (
          <>
            <div className="reports-hourly-chart">
              {hourlyHours.map((hour) => {
                const b = hourlyBuckets[hour];
                const pct = b.total ? (b.onTime / b.total) * 100 : 0;
                const tone = pct >= 90 ? "ok" : pct >= 75 ? "warn" : "crit";
                return (
                  <div className="reports-hourly-col" key={hour}>
                    <div className="reports-hourly-bar-wrap" title={`${Math.round(pct)}% on time · ${b.total} stops`}>
                      <div className={`reports-hourly-bar ${tone}`} style={{ height: `${Math.max(4, pct)}%` }} />
                    </div>
                    <span className="reports-hourly-tick">{String(hour).padStart(2, "0")}</span>
                  </div>
                );
              })}
            </div>
            <div className="reports-hourly-legend">
              <span><span className="reports-legend-dot ok" /> ≥ 90% on time</span>
              <span><span className="reports-legend-dot warn" /> 75 – 90%</span>
              <span><span className="reports-legend-dot crit" /> &lt; 75%</span>
            </div>
          </>
        )}
      </section>

      <h2 className="reports-subheading">Custom report browser</h2>

      <div className="card" style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <label>
          Report
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as ReportKind)}
            style={{ marginLeft: 8 }}
          >
            <option value="stop-events">Stop in/out events</option>
            <option value="trip-summary">Trip summary</option>
          </select>
        </label>
        <label>From <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></label>
        <label>To   <input type="date" value={dateTo}   onChange={(e) => setDateTo(e.target.value)} /></label>

        <label>Route
          <select value={routeId} onChange={(e) => setRouteId(e.target.value)}>
            <option value="">All</option>
            {routes.map((r) => <option key={r.id} value={r.id}>{r.route_short_name || r.route_long_name}</option>)}
          </select>
        </label>
        <label>Vehicle
          <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">All</option>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_label || v.vehicle_id}</option>)}
          </select>
        </label>
        <label>Driver
          <select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
            <option value="">All</option>
            {drivers.map((d) => <option key={d.id} value={d.id}>{[d.first_name, d.last_name].filter(Boolean).join(" ") || d.employee_id}</option>)}
          </select>
        </label>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="btn" onClick={runReport} disabled={loading}>{loading ? "Running…" : "Run report"}</button>
          <button className="btn ghost" onClick={downloadCsv} disabled={loading}>Download CSV</button>
        </div>
      </div>

      <div className="card" style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <strong>Stop In/Out (STIO)</strong>
        <span className="secondary-text">
          One workbook per route + direction for {dateTo}. Times come from detected
          movement; trips nobody declared still appear, under a system trip id.
        </span>
        <label>Direction
          <select
            value={stioDirection}
            onChange={(e) => setStioDirection(Number(e.target.value) === 1 ? 1 : 0)}
            style={{ marginLeft: 8 }}
          >
            <option value={0}>FWD</option>
            <option value={1}>BWD</option>
          </select>
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="btn ghost" onClick={runDetection} disabled={stioBusy}>
            {stioBusy ? "Working…" : "Re-run detection"}
          </button>
          <button className="btn" onClick={downloadStio} disabled={stioBusy}>Download XLSX</button>
        </div>
      </div>

      {stioNote && <div className="banner">{stioNote}</div>}
      {error && <div className="banner banner-error">{error}</div>}

      {kind === "stop-events" && stopRows.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th><th>Route</th><th>Trip</th><th>Driver</th><th>Vehicle</th>
              <th>#</th><th>Stop</th>
              <th>Sched arr</th><th>Actual arr</th><th>Δ</th>
              <th>Sched dep</th><th>Actual dep</th><th>Boarded</th><th>Alighted</th>
              <th>Src</th>
            </tr>
          </thead>
          <tbody>
            {stopRows.slice(0, 500).map((r, idx) => (
              <tr key={`${r.trip_instance_id}-${r.stop_sequence}-${idx}`}>
                <td>{r.service_date.slice(0, 10)}</td>
                <td>{r.route_short_name || "—"}</td>
                <td>{r.headsign || "—"}</td>
                <td>{r.driver_name || "—"}</td>
                <td>{r.vehicle_label || "—"}</td>
                <td>{r.stop_sequence}</td>
                <td>{r.stop_name || "—"} {r.stop_code ? <span className="secondary-text">#{r.stop_code}</span> : null}</td>
                <td>{fmtIsoTime(r.scheduled_arrival)}</td>
                <td>{fmtIsoTime(r.actual_arrival)}</td>
                <td>{r.arrival_delay_seconds != null ? `${Math.round(r.arrival_delay_seconds / 60)}m` : "—"}</td>
                <td>{fmtIsoTime(r.scheduled_departure)}</td>
                <td>{fmtIsoTime(r.actual_departure)}</td>
                <td>{r.passengers_boarded ?? "—"}</td>
                <td>{r.passengers_alighted ?? "—"}</td>
                <td className="secondary-text">{r.arrival_source || r.departure_source || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {kind === "trip-summary" && tripRows.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th><th>Route</th><th>Trip</th><th>Driver</th><th>Vehicle</th>
              <th>Status</th><th>Sched start</th><th>Actual start</th><th>Sched end</th><th>Actual end</th>
              <th>Stops</th><th>Served</th><th>Late</th><th>Avg Δ</th><th>Max Δ</th>
            </tr>
          </thead>
          <tbody>
            {tripRows.slice(0, 500).map((r) => (
              <tr key={r.trip_instance_id}>
                <td>{r.service_date.slice(0, 10)}</td>
                <td>{r.route_short_name || "—"}</td>
                <td>{r.headsign || "—"}</td>
                <td>{r.driver_name || "—"}</td>
                <td>{r.vehicle_label || "—"}</td>
                <td>{r.status}</td>
                <td>{fmtIsoTime(r.scheduled_start)}</td>
                <td>{fmtIsoTime(r.actual_start)}</td>
                <td>{fmtIsoTime(r.scheduled_end)}</td>
                <td>{fmtIsoTime(r.actual_end)}</td>
                <td>{r.total_stops}</td>
                <td>{r.stops_served}</td>
                <td>{r.stops_late}</td>
                <td>{r.avg_delay_seconds != null ? `${Math.round(r.avg_delay_seconds / 60)}m` : "—"}</td>
                <td>{r.max_delay_seconds != null ? `${Math.round(r.max_delay_seconds / 60)}m` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && stopRows.length === 0 && tripRows.length === 0 && (
        <div className="empty">No rows. Pick a window and click <strong>Run report</strong>.</div>
      )}
      {(stopRows.length > 500 || tripRows.length > 500) && (
        <div className="banner">Showing first 500 rows. Download CSV for the full result.</div>
      )}
    </div>
  );
}
